/**
 * Overview data, fetched once and held for the session.
 *
 * The Overview fires nine endpoints and takes around 30 seconds. It used to keep
 * the results in its own component state, so every visit — not just the first —
 * paid the full wait. This module holds them outside the component instead:
 * Layout starts the fetch in the background at sign-in, and the Overview reads
 * the result, so by the time someone clicks it the data is already there.
 *
 * Only the fetch lives here. The Overview still turns these settled results into
 * its own state, exactly as before, so its ~300 lines of mapping are untouched.
 *
 * The cache is keyed by user, and cleared on logout. Two people sharing a browser
 * must never see each other's business numbers, so a result fetched for one
 * user is never handed to another — including a request still in flight when
 * the first one signed out.
 */

import apiService from './api';

export type OverviewResults = PromiseSettledResult<unknown>[];

interface Entry {
  owner: string;
  at: number;
  results: OverviewResults;
}

/** How old a cached result may be before the Overview refreshes it in the background. */
export const OVERVIEW_STALE_MS = 5 * 60 * 1000;

let cached: Entry | null = null;
let inflight: { owner: string; promise: Promise<OverviewResults> } | null = null;
// Bumped on every clear, so a request that started before a logout can never
// write its result into the next user's session.
let generation = 0;

/** The nine Overview endpoints, in the order DashboardOverview destructures them. */
export function fetchOverviewBundle(): Promise<OverviewResults> {
  return Promise.allSettled([
    apiService.getDashboardOverview(),
    apiService.getSystemHealth(),
    apiService.getCommunicationMetrics(),
    apiService.getIntegratedAIAWSDashboard('last7days'),
    apiService.getRecentActivity(),
    apiService.getEnhancedRevenueAnalytics(),
    apiService.getGrossMarginAnalytics(),
    apiService.getAWSCostAnalytics(),
    apiService.get('/api/admin/subscriptions/intelligence'),
  ]);
}

/**
 * The Overview's data for this user. Shares a request already in flight, and
 * returns a cached result when one exists, unless `force` is set (the Refresh
 * button). `fetcher` is injectable for tests.
 */
export function prefetchOverview(
  owner: string,
  { force = false, fetcher = fetchOverviewBundle }: { force?: boolean; fetcher?: () => Promise<OverviewResults> } = {},
): Promise<OverviewResults> {
  if (!owner) return Promise.reject(new Error('prefetchOverview needs a user'));
  if (!force && inflight && inflight.owner === owner) return inflight.promise;
  if (!force && cached && cached.owner === owner) return Promise.resolve(cached.results);

  const started = generation;
  const promise = fetcher().then((results) => {
    if (generation === started) {
      cached = { owner, at: Date.now(), results };
      if (inflight && inflight.promise === promise) inflight = null;
    }
    return results;
  }, (err) => {
    if (inflight && inflight.promise === promise) inflight = null;
    throw err;
  });
  inflight = { owner, promise };
  return promise;
}

/** The cached result for this user, if any — synchronous, for an instant render. */
export function peekOverview(owner: string): { results: OverviewResults; at: number } | null {
  if (!owner || !cached || cached.owner !== owner) return null;
  return { results: cached.results, at: cached.at };
}

/** True while a request for this user is outstanding. */
export function isOverviewLoading(owner: string): boolean {
  return Boolean(owner && inflight && inflight.owner === owner);
}

export function isOverviewStale(at: number, now: number = Date.now()): boolean {
  return now - at > OVERVIEW_STALE_MS;
}

/** On logout. Also orphans any request still in flight. */
export function clearOverviewCache(): void {
  generation += 1;
  cached = null;
  inflight = null;
}

/** The identity the cache is keyed by. */
export function overviewOwner(user: { id?: string | number; email?: string } | null | undefined): string {
  if (!user) return '';
  return String(user.id ?? user.email ?? '');
}
